import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import {
  getGeneratePdfConfig,
  pdfSourceFieldsSchema,
} from 'app/shared/components/loanManagement/loanActionsGroup/LoanActionsGroup.utils';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { PdfSourceTypeEnum } from 'constants/loanActionEditor';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getConditionalArray } from 'utils/data';

import { getFormBundlesAction, getFormsAction } from 'store/forms/actions';
import { selectFormBundlesOptions, selectFormsListByGroupNameOptions } from 'store/forms/selectors';

const GeneratePdfSourceTypeForm = ({ setIsLoading, isLoading, formRef, onSubmit }) => {
  const dispatch = useDispatch();

  const formsOptions = useSelector(selectFormsListByGroupNameOptions);
  const formBundlesOptions = useSelector(selectFormBundlesOptions);

  const {
    form,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    validationErrors,
    handleChange,
    setValidationErrors,
    setForm,
  } = useForm({});

  const sourceTypeValue = form.sourceType?.value;

  const generatePdfConfig = getGeneratePdfConfig({
    areFieldsDisabled: isLoading,
    withPreSelectedCheckbox: false,
    sourceTypeValue,
    formsOptions,
    formBundlesOptions,
  });

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getFormsAction({ isDeleted: false })),
      dispatch(getFormBundlesAction()),
    ]).finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  useEffect(() => {
    setSchema(pdfSourceFieldsSchema);
  }, [setSchema]);

  useEffect(() => {
    const fieldsToOmit = [
      ...getConditionalArray(!sourceTypeValue, ['sourceType']),
      ...getConditionalArray(sourceTypeValue !== PdfSourceTypeEnum.FORM, ['form']),
      ...getConditionalArray(sourceTypeValue !== PdfSourceTypeEnum.FORM_BUNDLE, ['formBundle']),
    ];

    setForm(prev => _.omit(prev, fieldsToOmit));
    setValidationErrors(prev => _.omit(prev, fieldsToOmit));
  }, [sourceTypeValue, setForm, setValidationErrors]);

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      onSubmit(form).finally(() => setIsLoading(false));
    },
    [validateForm, form, onSubmit, setIsLoading],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="generate-pdf-source-type-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Submit"
      onSubmit={onSubmitForm}
    >
      <FormGenerator
        isNestedForm
        config={generatePdfConfig}
        form={form}
        handleChange={handleChange}
        numberOfColumns={1}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </ModalFormWrapper>
  );
};

GeneratePdfSourceTypeForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(GeneratePdfSourceTypeForm);
